/**
 * Randomize a hole: wipe content, then generate green, tees, fairway, rough, bunkers, waste, trees.
 */

import type { Course, Point, Par } from './types';
import { PAR_DIMENSIONS, getHolePar, setHolePar, clearHoleContent, applyDimensionsFromPars } from './courseState';
import { addSurfaceShape, addTee, addTree, addPin, addTeePosition, addMarker150 } from './courseState';
import { polygonOffsetOutward, polygonDifference } from './clipperOffset';
import { pointInPolygon } from './strokeToPolygon';
import type { TeePositionColorKey } from './types';

const YARD_TO_MM = 914.4;
const SQ_FT_TO_MM2 = (1 / 9) * (YARD_TO_MM * YARD_TO_MM); // 1 sq ft = 1/9 sq yd, 1 sq yd = YARD_TO_MM^2 mm^2

function random(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

function randomInt(min: number, max: number): number {
  return Math.floor(random(min, max + 1));
}

/** Polygon area in mm² (shoelace formula). */
function polygonAreaMm2(points: Point[]): number {
  if (points.length < 3) return 0;
  let sum = 0;
  for (let i = 0; i < points.length; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % points.length]!;
    sum += a[0] * b[1] - b[0] * a[1];
  }
  return Math.abs(sum) * 0.5;
}

const NATURAL_VERTS = 32;

/** Create a smooth polygon from radius function r(angle). Returns points scaled to target area. */
function createSmoothPolygon(
  cx: number,
  cy: number,
  areaMm2: number,
  radiusFn: (angle: number) => number,
  numVertices = NATURAL_VERTS
): Point[] {
  const points: Point[] = [];
  for (let i = 0; i < numVertices; i++) {
    const angle = (i / numVertices) * Math.PI * 2 - Math.PI / 2;
    const r = radiusFn(angle);
    points.push([cx + Math.cos(angle) * r, cy + Math.sin(angle) * r]);
  }
  const actual = polygonAreaMm2(points);
  if (actual > 0) {
    const scale = Math.sqrt(areaMm2 / actual);
    return points.map((p) => [cx + (p[0] - cx) * scale, cy + (p[1] - cy) * scale]);
  }
  return points;
}

/** Ellipse: rx and ry can differ for oblong shapes. */
function createEllipsePolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const rx = r * random(0.7, 1.4);
  const ry = r * random(0.7, 1.4);
  return createSmoothPolygon(cx, cy, areaMm2, (angle) => {
    const denom = (Math.cos(angle) / rx) ** 2 + (Math.sin(angle) / ry) ** 2;
    return 1 / Math.sqrt(denom);
  }, numVertices);
}

/** Circle: nearly circular (most circular). */
function createCirclePolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const wobble = random(0.92, 1.08);
  return createSmoothPolygon(cx, cy, areaMm2, () => r * wobble, numVertices);
}

/** Peanut: two lobes with a narrow waist. */
function createPeanutPolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const pinch = random(0.15, 0.35);
  return createSmoothPolygon(cx, cy, areaMm2, (angle) => {
    const lobe = Math.cos(2 * angle);
    return r * (1 - pinch * lobe);
  }, numVertices);
}

/** Pear: wider at bottom, narrower at top. */
function createPearPolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const bulge = random(0.2, 0.4);
  return createSmoothPolygon(cx, cy, areaMm2, (angle) => {
    const sinA = Math.sin(angle);
    return r * (1 + bulge * (1 - sinA));
  }, numVertices);
}

/** Organic blob: smooth radius variation (natural irregular shape). */
function createOrganicBlobPolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const a1 = random(0.05, 0.15);
  const a2 = random(0.05, 0.12);
  const a3 = random(0.03, 0.1);
  const ph1 = random(0, Math.PI * 2);
  const ph2 = random(0, Math.PI * 2);
  const ph3 = random(0, Math.PI * 2);
  return createSmoothPolygon(cx, cy, areaMm2, (angle) => {
    return r * (1 + a1 * Math.sin(2 * angle + ph1) + a2 * Math.sin(3 * angle + ph2) + a3 * Math.sin(4 * angle + ph3));
  }, numVertices);
}

/** Pick a random natural shape (ellipse, circle, peanut, pear, organic blob). */
function createNaturalShapePolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const fns = [
    () => createEllipsePolygon(cx, cy, areaMm2, numVertices),
    () => createCirclePolygon(cx, cy, areaMm2, numVertices),
    () => createPeanutPolygon(cx, cy, areaMm2, numVertices),
    () => createPearPolygon(cx, cy, areaMm2, numVertices),
    () => createOrganicBlobPolygon(cx, cy, areaMm2, numVertices),
  ];
  return fns[randomInt(0, fns.length - 1)]!();
}

/** Rotate a point around (cx, cy) by angle radians. */
function rotatePoint(cx: number, cy: number, px: number, py: number, angle: number): Point {
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const dx = px - cx;
  const dy = py - cy;
  return [cx + dx * cos - dy * sin, cy + dx * sin + dy * cos];
}

/**
 * Green shape: typically elongated ellipse with slant (left/right) or between circle and rectangle.
 * Bias: elongated circle (ellipse) > circle > rounded rectangle.
 */
function createGreenShapePolygon(cx: number, cy: number, areaMm2: number, numVertices = NATURAL_VERTS): Point[] {
  const r = Math.sqrt(areaMm2 / Math.PI);
  const roll = Math.random();
  let points: Point[];

  if (roll < 0.5) {
    // Elongated ellipse (most common) – slant left or right
    const slantAngle = random(-0.6, 0.6);
    const rx = r * random(1.1, 1.6);
    const ry = r * random(0.65, 0.95);
    points = createSmoothPolygon(cx, cy, areaMm2, (angle) => {
      const denom = (Math.cos(angle) / rx) ** 2 + (Math.sin(angle) / ry) ** 2;
      return 1 / Math.sqrt(denom);
    }, numVertices);
    points = points.map((p) => rotatePoint(cx, cy, p[0], p[1], slantAngle));
  } else if (roll < 0.75) {
    // Between circle and rectangle (superellipse)
    const rx = r * random(1.0, 1.4);
    const ry = r * random(0.8, 1.2);
    const n = random(2.2, 3.5);
    const slantAngle = random(-0.4, 0.4);
    points = createSmoothPolygon(cx, cy, areaMm2, (angle) => {
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      const term = (Math.abs(c) / rx) ** n + (Math.abs(s) / ry) ** n;
      return (1 / term) ** (1 / n);
    }, numVertices);
    points = points.map((p) => rotatePoint(cx, cy, p[0], p[1], slantAngle));
  } else {
    // Nearly circular with slight slant
    const slantAngle = random(-0.35, 0.35);
    const wobble = random(0.92, 1.08);
    points = createSmoothPolygon(cx, cy, areaMm2, () => r * wobble, numVertices);
    points = points.map((p) => rotatePoint(cx, cy, p[0], p[1], slantAngle));
  }

  return points;
}

/** Create a curved fairway for par 4/5 (middle 1/3, width up to 50%). Elliptical/organic, no straight edges. */
function createFairwayPolygon(
  halfW: number,
  h: number,
  minCoverage = 0.75,
  maxWidthFrac = 0.5
): Point[] {
  const midY0 = h / 3;
  const midY1 = (2 * h) / 3;
  const midH = midY1 - midY0;
  const width = halfW * 2 * random(maxWidthFrac * 0.5, maxWidthFrac);
  const halfWidth = width / 2;
  const centerX = random(-halfW * 0.3, halfW * 0.3);
  const coverH = midH * random(minCoverage, 1);
  const midY = (midY0 + midY1) / 2;
  const y0 = midY - coverH / 2;
  const y1 = midY + coverH / 2;
  const rx = halfWidth * random(0.9, 1.1);
  const ry = (y1 - y0) / 2 * random(0.9, 1.1);
  const numVertices = NATURAL_VERTS;
  const points: Point[] = [];
  for (let i = 0; i < numVertices; i++) {
    const angle = (i / numVertices) * Math.PI * 2;
    const wobble = 1 + random(-0.08, 0.08);
    points.push([
      centerX + Math.cos(angle) * rx * wobble,
      midY + Math.sin(angle) * ry * wobble,
    ]);
  }
  return points;
}

/** Create a large organic water body (pond/lake) - blob shape with wavy boundary. */
function createOrganicWaterBody(
  cx: number,
  cy: number,
  widthMm: number,
  heightMm: number,
  numVertices = 36
): Point[] {
  const rx = widthMm / 2;
  const ry = heightMm / 2;
  const a1 = random(0.08, 0.2);
  const a2 = random(0.05, 0.15);
  const a3 = random(0.03, 0.1);
  const ph1 = random(0, Math.PI * 2);
  const ph2 = random(0, Math.PI * 2);
  const ph3 = random(0, Math.PI * 2);
  const points: Point[] = [];
  for (let i = 0; i < numVertices; i++) {
    const angle = (i / numVertices) * Math.PI * 2 - Math.PI / 2;
    const wobble =
      1 + a1 * Math.sin(2 * angle + ph1) + a2 * Math.sin(3 * angle + ph2) + a3 * Math.sin(4 * angle + ph3);
    points.push([
      cx + Math.cos(angle) * rx * wobble,
      cy + Math.sin(angle) * ry * wobble,
    ]);
  }
  return points;
}

/** Create a meandering stream/river that crosses the grid. horizontal=true: flows left-right. */
function createRiverStream(
  halfW: number,
  h: number,
  horizontal: boolean,
  widthYd: number
): Point[] {
  const widthMm = widthYd * YARD_TO_MM;
  const n = 40;
  const numBends = randomInt(4, 8);
  const amp = (horizontal ? h : halfW * 2) * 0.12;
  const ph = random(0, Math.PI * 2);
  const centerY = h * random(0.35, 0.65);
  const centerX = random(-halfW * 0.2, halfW * 0.2);
  const points: Point[] = [];
  const halfWidth = widthMm / 2;

  if (horizontal) {
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const x = -halfW + t * halfW * 2;
      const wave = Math.sin(t * numBends * Math.PI + ph) * amp;
      const y = centerY + wave;
      points.push([x, y - halfWidth]);
    }
    for (let i = n; i >= 0; i--) {
      const t = i / n;
      const x = -halfW + t * halfW * 2;
      const wave = Math.sin(t * numBends * Math.PI + ph) * amp;
      const y = centerY + wave;
      points.push([x, y + halfWidth]);
    }
  } else {
    for (let i = 0; i <= n; i++) {
      const t = i / n;
      const y = t * h;
      const wave = Math.sin(t * numBends * Math.PI + ph) * amp;
      const x = centerX + wave;
      points.push([x - halfWidth, y]);
    }
    for (let i = n; i >= 0; i--) {
      const t = i / n;
      const y = t * h;
      const wave = Math.sin(t * numBends * Math.PI + ph) * amp;
      const x = centerX + wave;
      points.push([x + halfWidth, y]);
    }
  }
  return points;
}

/** Create a curved waste strip along an edge (no straight lines). */
function createCurvedWasteStrip(
  left: number,
  right: number,
  bottom: number,
  top: number,
  numWaves = 4
): Point[] {
  const points: Point[] = [];
  const n = Math.max(20, numWaves * 6);
  const width = right - left;
  const height = top - bottom;
  const amp = Math.min(width, height) * random(0.06, 0.15);
  const ph = random(0, Math.PI * 2);
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const y = bottom + t * height;
    const wave = Math.sin(t * numWaves * Math.PI + ph) * amp;
    points.push([left + wave, y]);
  }
  for (let i = n; i >= 0; i--) {
    const t = i / n;
    const y = bottom + t * height;
    const wave = Math.sin(t * numWaves * Math.PI + ph) * amp;
    points.push([right + wave, y]);
  }
  return points;
}

/** Centroid of polygon. */
function polygonCentroid(points: Point[]): Point {
  if (points.length === 0) return [0, 0];
  let sx = 0;
  let sy = 0;
  for (const p of points) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / points.length, sy / points.length];
}

/** Farthest intersection of ray (cx,cy) + t*(cos(a),sin(a)) with polygon boundary. Returns distance or 0. */
function rayPolygonRadius(cx: number, cy: number, angle: number, points: Point[]): number {
  const dx = Math.cos(angle);
  const dy = Math.sin(angle);
  let best = 0;
  const n = points.length;
  for (let i = 0; i < n; i++) {
    const a = points[i]!;
    const b = points[(i + 1) % n]!;
    const ex = b[0] - a[0];
    const ey = b[1] - a[1];
    const denom = ex * dy - ey * dx;
    if (Math.abs(denom) < 1e-10) continue;
    const t = ((a[1] - cy) * ex - (a[0] - cx) * ey) / denom;
    const s = (dx * (a[1] - cy) - dy * (a[0] - cx)) / denom;
    if (t > 0 && s >= 0 && s <= 1) {
      if (t > best) best = t;
    }
  }
  return best;
}

/** Add water ring around green by creating wedge polygons between inner and outer. */
function addWaterRingAroundGreen(
  course: Course,
  innerPoints: Point[],
  outerPoints: Point[],
  holeNum: number
): void {
  const [cx, cy] = polygonCentroid(innerPoints);
  const n = 24;
  const inner: Point[] = [];
  const outer: Point[] = [];
  for (let i = 0; i < n; i++) {
    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const ri = rayPolygonRadius(cx, cy, angle, innerPoints);
    const ro = rayPolygonRadius(cx, cy, angle, outerPoints);
    if (ri > 0 && ro > ri) {
      inner.push([cx + Math.cos(angle) * ri, cy + Math.sin(angle) * ri]);
      outer.push([cx + Math.cos(angle) * ro, cy + Math.sin(angle) * ro]);
    }
  }
  if (inner.length < 3 || outer.length < 3) return;
  const m = Math.min(inner.length, outer.length);
  for (let i = 0; i < m; i++) {
    const i0 = i;
    const i1 = (i + 1) % m;
    const wedge: Point[] = [
      inner[i0]!,
      inner[i1]!,
      outer[i1]!,
      outer[i0]!,
    ];
    if (polygonAreaMm2(wedge) > 1000) {
      addSurfaceShape(course, 'water', { type: 'polygon', points: wedge, holeNumber: holeNum });
    }
  }
}

/** Distance from point to line segment. */
function distToSegment(p: Point, a: Point, b: Point): number {
  const [px, py] = p;
  const [ax, ay] = a;
  const [bx, by] = b;
  const abx = bx - ax;
  const aby = by - ay;
  const apx = px - ax;
  const apy = py - ay;
  const ab2 = abx * abx + aby * aby;
  let t = (apx * abx + apy * aby) / (ab2 || 1);
  t = Math.max(0, Math.min(1, t));
  const nearestX = ax + t * abx;
  const nearestY = ay + t * aby;
  return Math.hypot(px - nearestX, py - nearestY);
}

/** Minimum distance from point to any edge of polygon. */
function distToPolygon(p: Point, polygon: Point[]): number {
  if (polygon.length < 2) return Infinity;
  let min = Infinity;
  const n = polygon.length;
  for (let i = 0; i < n; i++) {
    const a = polygon[i]!;
    const b = polygon[(i + 1) % n]!;
    const d = distToSegment(p, a, b);
    if (d < min) min = d;
  }
  return min;
}

/** Check if point is within corridor connecting tees to fairway (avoid trees here). */
function inTeeFairwayCorridor(
  p: Point,
  teeCenters: Point[],
  fairwayMid: Point,
  marginMm: number
): boolean {
  for (const tee of teeCenters) {
    if (distToSegment(p, tee, fairwayMid) < marginMm) return true;
  }
  return false;
}

export function randomizeHole(course: Course, holeNumber: number): void {
  clearHoleContent(course, holeNumber);

  const par = getHolePar(course, holeNumber) as Par;
  const { widthYd, heightYd } = PAR_DIMENSIONS[par];
  const w = widthYd * YARD_TO_MM;
  const h = heightYd * YARD_TO_MM;
  const halfW = w / 2;
  const margin = 0.1 * w;
  const innerMinX = -halfW + margin;
  const innerMaxX = halfW - margin;

  const holeNum = holeNumber;

  // Top 1/3: green zone (y from 2h/3 to h). Par 3: ensure greenY >= 260 yd so black tee fits.
  const topThirdY0 = (2 * h) / 3;
  const topThirdY1 = h;
  const minGreenY = par === 3 ? 260 * YARD_TO_MM : topThirdY0;
  const greenY = random(
    Math.max(topThirdY0 + (topThirdY1 - topThirdY0) * 0.2, minGreenY),
    topThirdY1 - (topThirdY1 - topThirdY0) * 0.2
  );
  const greenX = random(innerMinX, innerMaxX);
  const greenAreaMm2 = random(9000 * SQ_FT_TO_MM2, 90000 * SQ_FT_TO_MM2);
  const greenPoints = createGreenShapePolygon(greenX, greenY, greenAreaMm2);
  addSurfaceShape(course, 'green', { type: 'polygon', points: greenPoints, holeNumber: holeNum });

  const isIslandGreen = Math.random() < 0.2;
  if (isIslandGreen) {
    const gapFromGreen = 5 * YARD_TO_MM;
    const minWaterWidth = 30 * YARD_TO_MM;
    const innerRing = polygonOffsetOutward(greenPoints, gapFromGreen);
    const outerRing = polygonOffsetOutward(greenPoints, gapFromGreen + minWaterWidth);
    if (innerRing.length >= 3 && outerRing.length >= 3) {
      addWaterRingAroundGreen(course, innerRing, outerRing, holeNum);
    }
  }

  const greenCenter: Point = [greenX, greenY];
  const FRINGE_OFFSET_MM = 914.4;
  const fringeRing = polygonOffsetOutward(greenPoints, FRINGE_OFFSET_MM);
  const greenAndFringe = fringeRing.length >= 3 ? fringeRing : greenPoints;

  const MIN_PIN_FROM_EDGE_MM = 10 * 304.8;
  const MIN_PIN_SPACING_MM = 15 * 304.8;
  const pinDays: Array<'thu' | 'fri' | 'sat' | 'sun'> = ['thu', 'fri', 'sat', 'sun'];
  const placedPins: Point[] = [];
  const [gMinX, gMinY] = greenPoints.reduce(
    (a, p) => [Math.min(a[0], p[0]), Math.min(a[1], p[1])] as Point,
    [Infinity, Infinity] as Point
  );
  const [gMaxX, gMaxY] = greenPoints.reduce(
    (a, p) => [Math.max(a[0], p[0]), Math.max(a[1], p[1])] as Point,
    [-Infinity, -Infinity] as Point
  );

  for (const day of pinDays) {
    let placed = false;
    for (let attempt = 0; attempt < 80 && !placed; attempt++) {
      const px = random(gMinX, gMaxX);
      const py = random(gMinY, gMaxY);
      const pt: Point = [px, py];
      if (!pointInPolygon(pt, greenPoints)) continue;
      if (distToPolygon(pt, greenPoints) < MIN_PIN_FROM_EDGE_MM) continue;
      if (placedPins.some((p) => Math.hypot(pt[0] - p[0], pt[1] - p[1]) < MIN_PIN_SPACING_MM)) continue;
      addPin(course, { x: pt[0], y: pt[1], holeNumber: holeNum, day });
      placedPins.push(pt);
      placed = true;
    }
    if (!placed) {
      const fallback = polygonCentroid(greenPoints);
      addPin(course, { x: fallback[0], y: fallback[1], holeNumber: holeNum, day });
    }
  }

  const addWaterExcludingGreen = (waterPoints: Point[]) => {
    const parts = polygonDifference(waterPoints, greenAndFringe);
    for (const part of parts) {
      if (part.length >= 3 && polygonAreaMm2(part) > 1000) {
        addSurfaceShape(course, 'water', { type: 'polygon', points: part, holeNumber: holeNum });
      }
    }
  };

  if (Math.random() < 0.5) {
    const waterType = randomInt(0, 2);
    if (waterType === 0) {
      const side = Math.random() < 0.5 ? -1 : 1;
      const cx = side * halfW * random(0.5, 0.85);
      const cy = h * random(0.3, 0.7);
      const wMm = halfW * random(0.4, 0.7);
      const waterPoints = createOrganicWaterBody(cx, cy, wMm, h * random(0.5, 0.9));
      addWaterExcludingGreen(waterPoints);
    } else if (waterType === 1) {
      const cxL = -halfW * random(0.5, 0.8);
      const cxR = halfW * random(0.5, 0.8);
      const cy = h * random(0.35, 0.65);
      const wMm = halfW * random(0.35, 0.6);
      const leftWater = createOrganicWaterBody(cxL, cy, wMm, h * random(0.45, 0.8));
      const rightWater = createOrganicWaterBody(cxR, cy, wMm, h * random(0.45, 0.8));
      addWaterExcludingGreen(leftWater);
      addWaterExcludingGreen(rightWater);
    } else {
      const horizontal = Math.random() < 0.5;
      const streamWidthYd = random(12, 28);
      const streamPoints = createRiverStream(halfW, h, horizontal, streamWidthYd);
      addWaterExcludingGreen(streamPoints);
    }
  }

  const waterPolygons = (course.surfaces.water ?? []).filter(
    (s) => (s.holeNumber ?? holeNum) === holeNum && s.points.length >= 3
  );

  function teeOverlapsObstacles(teePoints: Point[]): boolean {
    const cen = teePoints.reduce((a, p) => [a[0] + p[0], a[1] + p[1]] as Point, [0, 0] as Point);
    cen[0] /= teePoints.length;
    cen[1] /= teePoints.length;
    if (pointInPolygon(cen, greenAndFringe)) return true;
    for (const p of teePoints) {
      if (pointInPolygon(p, greenAndFringe)) return true;
    }
    for (const wp of waterPolygons) {
      if (pointInPolygon(cen, wp.points)) return true;
      for (const p of teePoints) {
        if (pointInPolygon(p, wp.points)) return true;
      }
    }
    return false;
  }

  // Tee zone: 2–4 tee boxes with random horizontal spread; multiple tee positions share boxes
  const teeColors: TeePositionColorKey[] = ['red', 'gold', 'white', 'gray', 'blue', 'black'];
  const teeCenters: Point[] = [];

  const maxBlackYd = par === 3 ? 260 : par === 4 ? 400 : 550;
  const minRedYd = par === 3 ? 60 : par === 4 ? 100 : 150;
  const maxRedYd = par === 3 ? 200 : par === 4 ? 280 : 380;

  const targetDistancesYd: number[] = [];
  let d = random(minRedYd, maxRedYd);
  targetDistancesYd.push(d);
  for (let i = 1; i < 6; i++) {
    const minNext = d + 15;
    const maxNext = i === 5 ? maxBlackYd : d + (maxBlackYd - d) * 0.6;
    d = random(minNext, maxNext);
    targetDistancesYd.push(Math.min(d, maxBlackYd));
  }

  const numTeeBoxes = randomInt(2, 4);
  const teeBoxSpreadX = halfW * 0.45;
  const assignments: { boxIdx: number; colorIdx: number }[] = [];
  for (let i = 0; i < 6; i++) {
    assignments.push({
      boxIdx: Math.min(Math.floor((i * numTeeBoxes) / 6), numTeeBoxes - 1),
      colorIdx: i,
    });
  }

  const teeBoxes: { points: Point[]; colors: TeePositionColorKey[] }[] = [];
  for (let b = 0; b < numTeeBoxes; b++) {
    const colorsInBox = assignments.filter((a) => a.boxIdx === b).map((a) => teeColors[a.colorIdx]!);
    if (colorsInBox.length === 0) continue;
    const distIdx = Math.floor((b + 0.5) * 6 / numTeeBoxes);
    const distYd = targetDistancesYd[Math.min(distIdx, 5)]!;
    const distMm = distYd * YARD_TO_MM;
    const teeY = Math.max(0, greenY - distMm);
    const teeX = greenX + random(-teeBoxSpreadX, teeBoxSpreadX);
    const teeAreaMm2 = random(4000 * SQ_FT_TO_MM2, 18000 * SQ_FT_TO_MM2);
    let teePoints: Point[] | null = null;
    for (let attempt = 0; attempt < 50; attempt++) {
      const tryX = greenX + random(-teeBoxSpreadX, teeBoxSpreadX);
      const pts = createNaturalShapePolygon(tryX, teeY, teeAreaMm2, 24);
      if (!teeOverlapsObstacles(pts)) {
        teePoints = pts;
        break;
      }
    }
    if (!teePoints) teePoints = createNaturalShapePolygon(teeX, teeY, teeAreaMm2, 24);
    addTee(course, { points: teePoints, holeNumber: holeNum, label: colorsInBox[0] ?? 'white' });
    teeBoxes.push({ points: teePoints, colors: colorsInBox });

    const [bxMin, byMin] = teePoints.reduce(
      (a, p) => [Math.min(a[0], p[0]), Math.min(a[1], p[1])] as Point,
      [Infinity, Infinity] as Point
    );
    const [bxMax, byMax] = teePoints.reduce(
      (a, p) => [Math.max(a[0], p[0]), Math.max(a[1], p[1])] as Point,
      [-Infinity, -Infinity] as Point
    );
    const usedInBox: Point[] = [];
    for (const color of colorsInBox) {
      let pos: Point | null = null;
      for (let attempt = 0; attempt < 40; attempt++) {
        const px = random(bxMin, bxMax);
        const py = random(byMin, byMax);
        const pt: Point = [px, py];
        if (!pointInPolygon(pt, teePoints)) continue;
        const tooClose = usedInBox.some((u) => Math.hypot(pt[0] - u[0], pt[1] - u[1]) < 2000);
        if (tooClose) continue;
        pos = pt;
        usedInBox.push(pt);
        break;
      }
      if (!pos) {
        const cen = polygonCentroid(teePoints);
        pos = [cen[0] + random(-2000, 2000), cen[1] + random(-2000, 2000)];
        usedInBox.push(pos);
      }
      teeCenters.push(pos);
      addTeePosition(course, { x: pos[0], y: pos[1], holeNumber: holeNum, color });
    }
  }

  const avgTeeX = teeCenters.reduce((s, c) => s + c[0], 0) / teeCenters.length;
  const avgTeeY = teeCenters.reduce((s, c) => s + c[1], 0) / teeCenters.length;
  const fairwayMid: Point = [(avgTeeX + greenCenter[0]) / 2, (avgTeeY + greenCenter[1]) / 2];

  const corridorMargin = 20 * YARD_TO_MM;

  if (par === 4 || par === 5) {
    const fairwayPoints = createFairwayPolygon(halfW, h, 0.75, 0.5);
    addSurfaceShape(course, 'fairway', { type: 'polygon', points: fairwayPoints, holeNumber: holeNum });
    const dx = avgTeeX - greenCenter[0];
    const dy = avgTeeY - greenCenter[1];
    const len = Math.hypot(dx, dy) || 1;
    const markerDist = 150 * YARD_TO_MM;
    const mx = greenCenter[0] + (dx / len) * markerDist;
    const my = greenCenter[1] + (dy / len) * markerDist;
    addMarker150(course, { x: mx, y: my, holeNumber: holeNum });
  }

  const firstCutWidth = random(5 * YARD_TO_MM, 20 * YARD_TO_MM);

  const addFirstCutAround = (points: Point[]) => {
    const offset = polygonOffsetOutward(points, firstCutWidth);
    if (offset.length >= 3) {
      addSurfaceShape(course, 'firstCut', { type: 'polygon', points: offset, holeNumber: holeNum });
    }
  };

  addFirstCutAround(greenPoints);
  for (const tee of course.tees) {
    if ((tee.holeNumber ?? 1) === holeNum && tee.points?.length >= 3) {
      addFirstCutAround(tee.points);
    }
  }
  if (par === 4 || par === 5) {
    const fairway = course.surfaces.fairway?.find((s) => s.holeNumber === holeNum);
    if (fairway?.points) addFirstCutAround(fairway.points);
  }

  for (let i = 0; i < randomInt(5, 10); i++) {
    const by = random(h / 4, (3 * h) / 4);
    const bx = random(-halfW * 0.6, halfW * 0.6);
    const bunkerArea = random(1500 * SQ_FT_TO_MM2, 5000 * SQ_FT_TO_MM2);
    const bunkerPoints = createNaturalShapePolygon(bx, by, bunkerArea, 28);
    addSurfaceShape(course, 'bunker', { type: 'polygon', points: bunkerPoints, holeNumber: holeNum });
  }

  const wasteWidth = random(5 * YARD_TO_MM, 20 * YARD_TO_MM);
  if (Math.random() > 0.3) {
    const leftWaste = createCurvedWasteStrip(-halfW, -halfW + wasteWidth, 0, h, randomInt(3, 6));
    addSurfaceShape(course, 'waste', { type: 'polygon', points: leftWaste, holeNumber: holeNum });
  }
  if (Math.random() > 0.3) {
    const rightWaste = createCurvedWasteStrip(halfW - wasteWidth, halfW, 0, h, randomInt(3, 6));
    addSurfaceShape(course, 'waste', { type: 'polygon', points: rightWaste, holeNumber: holeNum });
  }
  if (Math.random() > 0.5) {
    const cutY = random(h * 0.3, h * 0.7);
    const cutW = Math.min(wasteWidth, 20 * YARD_TO_MM);
    const cutWaste = createCurvedWasteStrip(-halfW, halfW, cutY - cutW / 2, cutY + cutW / 2, randomInt(4, 8));
    addSurfaceShape(course, 'waste', { type: 'polygon', points: cutWaste, holeNumber: holeNum });
  }

  const treeDiameterYd: number[] = [5, 10, 20];
  const numTrees = randomInt(3, 75);
  for (let i = 0; i < numTrees; i++) {
    let tx = random(-halfW * 0.9, halfW * 0.9);
    let ty = random(0, h);
    let attempts = 0;
    while (inTeeFairwayCorridor([tx, ty], teeCenters, fairwayMid, corridorMargin) && attempts < 20) {
      tx = random(-halfW * 0.9, halfW * 0.9);
      ty = random(0, h);
      attempts++;
    }
    const diameterYd = treeDiameterYd[randomInt(0, treeDiameterYd.length - 1)]!;
    const radiusMm = (diameterYd / 2) * YARD_TO_MM;
    addTree(course, tx, ty, '', radiusMm, holeNum);
  }
}

/** Fisher–Yates shuffle. */
function shuffle<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = randomInt(0, i);
    [out[i], out[j]] = [out[j]!, out[i]!];
  }
  return out;
}

/**
 * Randomize all holes: assign 4 par 3, 4 par 5, rest par 4 (randomly), then generate geometry for each.
 */
export function randomizeAll(course: Course): void {
  const holeCount = course.metadata.holeCount === 9 ? 9 : 18;
  const indices = shuffle([...Array(holeCount)].map((_, i) => i + 1));

  const par3Holes = indices.slice(0, 4);
  const par5Holes = indices.slice(4, 8);
  const par4Holes = indices.slice(8);

  for (const n of par3Holes) setHolePar(course, n, 3);
  for (const n of par5Holes) setHolePar(course, n, 5);
  for (const n of par4Holes) setHolePar(course, n, 4);

  applyDimensionsFromPars(course);

  for (let n = 1; n <= holeCount; n++) {
    randomizeHole(course, n);
  }
}

/**
 * Randomize all holes asynchronously, yielding between holes so the UI can update.
 * Calls onProgress(current, total) after each hole completes.
 */
export function randomizeAllAsync(
  course: Course,
  onProgress?: (current: number, total: number) => void
): Promise<void> {
  return new Promise((resolve) => {
    const holeCount = course.metadata.holeCount === 9 ? 9 : 18;
    const indices = shuffle([...Array(holeCount)].map((_, i) => i + 1));

    const par3Holes = indices.slice(0, 4);
    const par5Holes = indices.slice(4, 8);
    const par4Holes = indices.slice(8);

    for (const n of par3Holes) setHolePar(course, n, 3);
    for (const n of par5Holes) setHolePar(course, n, 5);
    for (const n of par4Holes) setHolePar(course, n, 4);

    applyDimensionsFromPars(course);

    let n = 0;
    function doNext() {
      if (n >= holeCount) {
        resolve();
        return;
      }
      n++;
      randomizeHole(course, n);
      onProgress?.(n, holeCount);
      setTimeout(doNext, 0);
    }
    doNext();
  });
}
